import { z } from 'zod'

/**
 * VFS's backend answers every request — success and failure alike — inside one
 * envelope. See the integration guide §3.
 *
 * `data` is deliberately `unknown` here rather than generic: the envelope is
 * parsed first so `success` and `errorCode` can be trusted, and the payload is
 * parsed second by whichever caller knows its shape. That ordering is what lets a
 * 400 with a `data: null` be turned into an ApiError without a schema for the
 * payload that never arrived.
 *
 * `looseObject`, not strict: `metadata` gains fields as the backend grows, and a
 * new one must not turn a successful write into a parse failure.
 */
export const vfsClientEnvelopeSchema = z.looseObject({
  success: z.boolean(),
  statusCode: z.number().int().optional(),
  data: z.unknown().optional(),
  message: z.string().nullish(),
  /** Short machine-readable code — `NOT_FOUND`, `VALIDATION_ERROR`, … */
  errorCode: z.string().nullish(),
  /** Field-level validation messages, already flattened to strings by the backend. */
  errors: z.array(z.string()).nullish(),
  correlationId: z.string().nullish(),
  /**
   * `sessionToken` is the one metadata field the client reads: the backend puts a
   * session JWT here whenever it issued one during the request (bootstrap, an
   * escalation, the sliding renewal), and the transport adopts it (ADR 0018).
   */
  metadata: z.looseObject({ sessionToken: z.string().nullish() }).nullish(),
})

export type VfsClientEnvelope = z.infer<typeof vfsClientEnvelopeSchema>

/**
 * A bare `{ message }` with no envelope around it.
 *
 * This was the API-key middleware's rejection, back when the API authenticated with
 * `X-Api-Key`. The JWT middleware that replaced it envelopes its 401 like everything
 * else (guide §2), so this is no longer the expected shape — it is kept as the
 * fallback for a rejection that never reaches a controller at all, which is what IIS
 * or a gateway in front of the app would produce. Recognised explicitly, because
 * parsing it as an envelope would fail on the missing `success` and report a schema
 * drift instead of "you are not authenticated".
 */
export const vfsClientAuthFailureSchema = z.object({ message: z.string() })

/**
 * §4.1a `POST /api/v1/session`. `sessionToken` is the session JWT the transport sends
 * back in `x-sid-key` (ADR 0018); the backend also sets it as a cookie, which the
 * gateway drops and this client does not use. `expiresAt`/`ttlSeconds` are
 * informational: the server is authoritative and the transport re-bootstraps on the
 * 401 that says the session lapsed.
 */
export const sessionResponseSchema = z.looseObject({
  expiresAt: z.string(),
  ttlSeconds: z.number(),
  sessionToken: z.string().min(1),
})

export type SessionResponse = z.infer<typeof sessionResponseSchema>

/** §4.2 — the only VFS Client write whose payload we read back. */
export const applicationDetailResponseSchema = z.looseObject({
  applicationDetailId: z.number().int(),
})

export type ApplicationDetailResponse = z.infer<typeof applicationDetailResponseSchema>

/**
 * §4.6 Step 6 — the officer's SSO login row.
 *
 * Only `vacAuthId` is named because only `vacAuthId` is used: it is the path segment
 * the Step 7 verification outcome is posted to. The rest of the row — the four
 * officer columns the backend extracts from `ssoProfile`, and `verificationStatus`,
 * which starts as `"Pending"` — is echoed back for confirmation and carried through
 * untyped by `looseObject`.
 */
export const vacAuthResponseSchema = z.looseObject({
  vacAuthId: z.number().int(),
})

export type VacAuthResponse = z.infer<typeof vacAuthResponseSchema>
